// Ranking for the Teams tab's Trending strip. Pure, so the rules can be tested
// without rendering the tab.
//
// The strip is a recommendation, so a team only appears when adding it could
// pay off this week:
//   - it plays this week (a bye scores nothing),
//   - its game has not locked (it could not be added anyway),
//   - it is not a big underdog. Being 2-0 is no reason to add a team that is a
//     21 point underdog on Saturday.
//
// Among those, the score is season form plus demand: average fantasy points
// per scored week, plus a bonus for each time the team was picked up across
// the game in the last week (trending_adds, migration 077). Ties go to the
// bigger favourite.
//
// Demand is never shown as a number. A raw count ("3 adds") tells managers how
// small the game is, so the card carries a qualitative tag instead, and only
// for the few most-added teams.

export const TRENDING_MAX_UNDERDOG = 10;
export const TRENDING_ADD_WEIGHT = 2;
export const TRENDING_SIZE = 8;
export const POPULAR_MIN_ADDS = 2;
export const POPULAR_TOP_N = 3;

const scoredWeeks = (team) =>
  Object.entries(team.currentSeason?.weeklyPoints || {}).filter(
    ([w, p]) => String(w) !== "0" && typeof p === "number"
  ).length;

export function averagePoints(team) {
  const weeks = scoredWeeks(team);
  return weeks ? (team.currentSeason?.gamePoints || 0) / weeks : 0;
}

// Negative spread means favoured, matching scoring.js. A game with no line yet
// is kept: missing data is not evidence of a mismatch.
export function isBigUnderdog(spread) {
  return typeof spread === "number" && spread > TRENDING_MAX_UNDERDOG;
}

// The slugs that earn the "Popular pickup" tag: the most-added teams across
// the game, and only once demand is real (two or more adds). Ties at the
// cut-off are all included rather than broken arbitrarily.
export function popularPickups(adds = {}) {
  const counts = Object.values(adds).filter((n) => n >= POPULAR_MIN_ADDS).sort((a, b) => b - a);
  if (!counts.length) return new Set();
  const floor = counts[Math.min(POPULAR_TOP_N, counts.length) - 1];
  return new Set(Object.keys(adds).filter((slug) => adds[slug] >= Math.max(floor, POPULAR_MIN_ADDS)));
}

export function rankTrending({ pool, slugOf, gamesBySlug, adds = {}, isLocked = () => false, hasSeasonPoints }) {
  const spreadOf = (slug) => gamesBySlug[slug]?.homeSpread;

  if (!hasSeasonPoints) {
    // Preseason: nobody has points or adds, so rank on the line alone,
    // biggest favourite first, then alphabetically before lines land.
    const favoured = pool
      .filter((t) => typeof spreadOf(slugOf(t)) === "number")
      .sort((a, b) => spreadOf(slugOf(a)) - spreadOf(slugOf(b)));
    const list = favoured.length ? favoured : [...pool].sort((a, b) => a.school.localeCompare(b.school));
    return list.slice(0, TRENDING_SIZE);
  }

  return pool
    .filter((t) => {
      const slug = slugOf(t);
      return gamesBySlug[slug] && !isLocked(slug) && !isBigUnderdog(spreadOf(slug));
    })
    .map((t) => {
      const slug = slugOf(t);
      return { team: t, score: averagePoints(t) + TRENDING_ADD_WEIGHT * (adds[slug] || 0), spread: spreadOf(slug) };
    })
    .sort((a, b) => b.score - a.score || (a.spread ?? 99) - (b.spread ?? 99))
    .slice(0, TRENDING_SIZE)
    .map((entry) => entry.team);
}
